import {
  FailedUsageEventDto,
  PriceModelDto,
  PriceModelTypeEnum,
  SubscriptionStatusEnum,
  UsageStatsDto,
} from '@shared/contracts';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  CreditCard,
  ExternalLink,
  RefreshCw,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { subscriptionsApi } from '../services/api';

export const SubscriptionPage: React.FC = () => {
  const { user, subscription, walletBalance, refreshSubscription } = useAuth();
  const [usageStats, setUsageStats] = useState<UsageStatsDto[]>([]);
  const [failedEvents, setFailedEvents] = useState<FailedUsageEventDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await refreshSubscription();
      const [statsData, failedData] = await Promise.all([
        subscriptionsApi.getStats('daily').catch(() => []),
        subscriptionsApi.getFailed().catch(() => []),
      ]);
      setUsageStats(statsData);
      setFailedEvents(failedData);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getStatusBadge = (status?: SubscriptionStatusEnum) => {
    switch (status) {
      case SubscriptionStatusEnum.ACTIVE:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Active Subscription
          </span>
        );
      case SubscriptionStatusEnum.TRIALING:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-sky-50 text-sky-700 border border-sky-200">
            <Clock className="w-3.5 h-3.5 text-sky-600" /> Trial Period
          </span>
        );
      case SubscriptionStatusEnum.PAST_DUE:
      case SubscriptionStatusEnum.UNPAID:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Payment Past Due
          </span>
        );
      case SubscriptionStatusEnum.CANCELED:
      case SubscriptionStatusEnum.INCOMPLETE_EXPIRED:
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
            <AlertCircle className="w-3.5 h-3.5 text-rose-600" /> No Active Subscription
          </span>
        );
    }
  };

  const formatPrice = (model: PriceModelDto) => {
    if (model.pricingType === PriceModelTypeEnum.PAY_AS_GO || model.pricingType === PriceModelTypeEnum.TIER_PAY_AS_GO) {
      return `€${(model.amount ?? 0.05).toFixed(4)} / unit`;
    }
    const euros = ((model.amount ?? 0) / 100).toFixed(2);
    return `€${euros} / ${model.interval || 'month'}`;
  };

  const marketplaceUrl = subscription?.marketplaceUrl || 'https://sensifai.com/marketplace/spi-avera';

  return (
    <div className="space-y-6 text-slate-800 font-sans pb-12">
      {/* Light Theme Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Subscription & Plan Management</h1>
            {getStatusBadge(subscription?.status)}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Logto Customer ID:{' '}
            <code className="text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded font-mono text-[11px]">
              {user?.sub || user?.id}
            </code>
          </p>
        </div>
        <button
          onClick={loadData}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl border border-slate-200 transition-all shadow-xs shrink-0"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-slate-600 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh Status
        </button>
      </div>

      {/* Warning Banner if No Active Subscription */}
      {(!subscription || !subscription.accessGranted) && (
        <div className="p-6 rounded-2xl bg-gradient-to-r from-amber-50/90 via-rose-50/70 to-orange-50/80 border border-amber-200/80 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100/80 text-amber-700 border border-amber-200/80 shrink-0">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="flex-1 space-y-1">
              <h3 className="text-sm font-bold text-slate-900">No Active Marketplace Subscription</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                {subscription?.message ||
                  'Your account does not currently have an active subscription for SensiAvera on the Sensifai Marketplace. Premium document intelligence features require an active subscription or prepaid wallet balance.'}
              </p>
            </div>
            <a
              href={marketplaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-xs transition-all shadow-sm shadow-indigo-600/20 shrink-0"
            >
              <span>Subscribe on Marketplace</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      )}

      {/* Top Cards: Plan Detail & Wallet Balance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Card 1: Subscription Overview */}
        <div className="lg:col-span-2 p-6 rounded-2xl bg-white border border-slate-200/80 shadow-sm space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-indigo-600" />
              Current Subscription Details
            </h2>
            {subscription?.subscription?.priceModel && (
              <span className="text-[11px] font-mono text-indigo-700 bg-indigo-50 px-2.5 py-0.5 rounded-full border border-indigo-100">
                {subscription.subscription.priceModel.pricingType}
              </span>
            )}
          </div>

          {subscription?.subscription ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-xl bg-slate-50/80 border border-slate-200/60 text-xs">
              <div>
                <span className="text-slate-400 block text-[11px] font-medium">Plan Name</span>
                <span className="font-semibold text-slate-800 mt-0.5 block truncate">
                  {subscription.subscription.priceModel?.name || 'Standard Application Plan'}
                </span>
              </div>
              <div>
                <span className="text-slate-400 block text-[11px] font-medium">Currency</span>
                <span className="font-semibold text-slate-800 mt-0.5 block uppercase">
                  {subscription.subscription.currency || 'EUR'}
                </span>
              </div>
              <div>
                <span className="text-slate-400 block text-[11px] font-medium">Current Period End</span>
                <span className="font-semibold text-slate-800 mt-0.5 block">
                  {subscription.subscription.currentPeriodEnd
                    ? (subscription.subscription.priceModelId === 'pm_avera_enterprise'
                        ? 'Unlimited / Permanent'
                        : new Date(subscription.subscription.currentPeriodEnd).toLocaleDateString())
                    : 'N/A'}
                </span>
              </div>
              <div>
                <span className="text-slate-400 block text-[11px] font-medium">Entitlement</span>
                <span
                  className={`font-semibold mt-0.5 block ${subscription.accessGranted ? 'text-emerald-600' : 'text-rose-600'
                    }`}
                >
                  {subscription.accessGranted ? 'Entitled (Granted)' : 'Restricted'}
                </span>
              </div>
            </div>
          ) : (
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-500">
              No active subscription object returned. Subscribe to attach a price model to your customer account.
            </div>
          )}

          <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
            <span className="text-slate-500">Need to upgrade or change seats?</span>
            <a
              href={marketplaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-semibold"
            >
              Manage on Marketplace <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        {/* Card 2: Prepaid Wallet Balance */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-500/10 via-white to-teal-50/60 border border-emerald-200/80 shadow-sm flex flex-col justify-between space-y-4">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-600" />
                Prepaid Wallet
              </h2>
              <span className="text-[11px] text-slate-400 font-medium">Gatekeeper Ledger</span>
            </div>

            <div className="p-4 rounded-xl bg-white/80 border border-emerald-100 shadow-xs">
              <span className="text-slate-500 text-xs block font-medium">Available Balance</span>
              <div className="text-3xl font-extrabold text-slate-900 mt-1 tracking-tight">
                {walletBalance?.balance && walletBalance.balance >= 999999
                  ? 'Unlimited'
                  : `€${(walletBalance?.balance ?? 0)}`}
              </div>
              <p className="text-[11px] text-slate-500 mt-2 leading-tight">
                Pay-as-you-go metered usage deducts in real-time from this ledger.
              </p>
            </div>
          </div>

          <a
            href={marketplaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-xs flex items-center justify-center gap-2 shadow-sm transition-all"
          >
            <span>Refill Wallet Credit</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* Usage Stats & Failed Events Monitor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Stats */}
        <div className="p-6 rounded-2xl bg-white border border-slate-200/80 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
              Metered Usage Stats (Gatekeeper)
            </h2>
            <span className="text-[11px] text-slate-400 font-medium">Daily Bucket</span>
          </div>

          {usageStats.length > 0 ? (
            <div className="space-y-2">
              {usageStats.map((stat, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200/80 text-xs"
                >
                  <span className="font-mono text-slate-700 font-medium">{stat.bucket}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-slate-500">Requests: {stat.total_requests}</span>
                    <span className="font-semibold text-indigo-600">Usage: {stat.total_usages}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-500 p-4 rounded-xl bg-slate-50 border border-slate-200 text-center">
              No reported usage events recorded for this period yet.
            </p>
          )}
        </div>

        {/* Failed Events Monitoring */}
        <div className="p-6 rounded-2xl bg-white border border-slate-200/80 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Activity className="w-4 h-4 text-rose-600" />
              Failed Usage Event Diagnostics
            </h2>
            <span className="text-[11px] text-slate-400 font-medium">Async Pipeline</span>
          </div>

          {failedEvents.length > 0 ? (
            <div className="space-y-2">
              {failedEvents.map((evt, idx) => (
                <div
                  key={idx}
                  className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-xs space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <code className="text-rose-700 font-mono font-semibold">{evt.identifier}</code>
                    <span className="text-slate-400 text-[10px]">
                      {new Date(evt.timestamp * 1000).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-slate-700 text-[11px]">{evt.failed_reason}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-center text-xs text-emerald-700 flex items-center justify-center gap-2 font-medium">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>All async usage processing events are clean (0 failures).</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
